import * as THREE from 'three';
import { ENV } from '../core/SimConfig.js';
import { clamp, DEG2RAD } from '../utils.js';

const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _fwd = new THREE.Vector3();
const _failVel = new THREE.Vector3();

/**
 * Kinematic fixed-wing flight model.
 *
 * Same shape as HelicopterFlightModel — AI sets desiredHeading / desiredSpeed /
 * desiredVerticalRate each frame, and the model chases them under per-axis
 * rate caps. Differences from the helicopter model:
 *
 *   - Visual pitch is the climb angle (atan(VR / forwardSpeed)) plus a small
 *     body angle of attack, instead of a forward-lean tied to speed.
 *   - Visual roll is the coordinated bank for the current yaw rate
 *     (atan(V·ω/g)), capped — produces the steep banks fighters use in turns.
 *   - Turn rate is limited by a load factor (`loadFactorLimit`) rather than a
 *     flat rate, so it falls off as speed rises. See update() step 1.
 *   - minSpeed is a hard floor (planes can't hover), so AI desiredSpeed is
 *     clamped well above zero.
 *
 * Force/torque integration is intentionally absent — these are AI traffic, not
 * the player. Structural-failure mode falls back to gravity + tumble.
 */
export default class FixedWingFlightModel {
  constructor(params) {
    this.params = params;

    // Physics state
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.heading = 0;          // yaw, radians, world frame
    this.forwardSpeed = params.cruiseSpeed; // start at cruise so we don't accelerate from 0
    this.verticalRate = 0;     // m/s
    this.yawRate = 0;          // rad/s

    // Visual state (spring-damper integrated)
    this.pitch = 0;
    this.roll = 0;
    this.pitchVel = 0;
    this.rollVel = 0;

    // Sum-of-sines sway state — per-instance random phase
    this._swayTime = 0;
    this._swayPhase = [
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
    ];

    // AI inputs — set externally each frame
    this.desiredHeading = 0;
    this.desiredSpeed = params.cruiseSpeed;
    this.desiredVerticalRate = 0;
    // Structural g budget for turning. Normally the sustained limit; a pilot
    // may raise it to the airframe's instantaneous limit for a defensive
    // break. The pilot rewrites this every tick, so a raised limit can't leak
    // past the maneuver that asked for it.
    this.loadFactorLimit = params.maxLoadFactor;

    // Damage modulation (parallels the heli model so EnemyPlane can use the
    // same damage pipeline once we wire weapons in).
    this.liftScale = 1;
    this.structuralFailure = false;
    this.angularVelocity = new THREE.Vector3();
    this.failureGravity = 9.81;

    // Fixed-wing failure state — set when EnemyPlane trips structuralFailure.
    // Roll spin around the body's own forward axis; optional flat-spin yaw on
    // top. Unlike the heli, the plane keeps its nose aligned with velocity
    // (no full mid-air breakup) — only the roll/yaw spin and pitch offset
    // diverge from level flight.
    this._failRollRate = 0;          // rad/s around body forward axis
    this._failYawRate = 0;           // rad/s — non-zero in flat-spin mode
    this._failRoll = 0;              // accumulated roll angle (rad)
    this._failYaw = 0;               // accumulated extra yaw angle (rad)
    this._failPitchOffset = 0;       // rad — flat-spin nose-up tilt
    // 0→1 ramp multiplier on failure forces. EnemyPlane sets rate when
    // structural failure trips; default 1 = forces apply at full strength.
    this._failTransition = 1;
    this._failTransitionRate = 0;    // 1/s — inverse of ramp-in duration

    this.quaternion = new THREE.Quaternion();
  }

  update(dt) {
    if (this.structuralFailure) {
      this._updateFailure(dt);
      return;
    }
    const p = this.params;

    // 1. Heading — chase a desired yaw rate, capped by the g budget. yawRate
    //    accelerates toward the target so banks ease in/out instead of
    //    snapping. Same formulation as the heli model.
    //
    //    The cap is a load factor, not a flat rate: a level coordinated turn
    //    at n g pulls sqrt(n²−1)·g laterally, so ω = g·sqrt(n²−1)/V. That
    //    makes turn rate fall off as the jet accelerates, which is the whole
    //    reason a fast jet can't also knife-fight — a flat rate cap let a
    //    240 m/s MiG pull 11 g through a break and disappear off the HUD.
    //    maxYawRate stays on as the control-authority ceiling, binding only
    //    down near stall speed where the g limit alone would allow a pirouette.
    let headingErr = this.desiredHeading - this.heading;
    while (headingErr >  Math.PI) headingErr -= Math.PI * 2;
    while (headingErr < -Math.PI) headingErr += Math.PI * 2;
    const n = Math.max(1, this.loadFactorLimit);
    const gYawRate = (ENV.gravity * Math.sqrt(n * n - 1)) / Math.max(this.forwardSpeed, 1);
    const yawCap = Math.min(p.maxYawRate, gYawRate);
    const desiredYawRate = clamp(headingErr * p.yawRateGain, -yawCap, yawCap);
    const yawAccelMax = p.yawAccel * dt;
    this.yawRate += clamp(desiredYawRate - this.yawRate, -yawAccelMax, yawAccelMax);
    this.heading += this.yawRate * dt;

    // 2. Forward speed — chase desiredSpeed, limited by forwardAccel.
    //    Hard-clamped to [minSpeed, maxSpeed] (planes can't hover).
    const speedErr = this.desiredSpeed - this.forwardSpeed;
    const maxSpeedDelta = p.forwardAccel * dt;
    this.forwardSpeed += clamp(speedErr, -maxSpeedDelta, maxSpeedDelta);
    this.forwardSpeed = clamp(this.forwardSpeed, p.minSpeed, p.maxSpeed);

    // 3. Vertical rate — chase desiredVerticalRate, limited by verticalAccel.
    const desiredV = Math.min(this.desiredVerticalRate, p.maxClimbRate * this.liftScale);
    const vertErr = desiredV - this.verticalRate;
    const maxVertDelta = p.verticalAccel * dt;
    this.verticalRate += clamp(vertErr, -maxVertDelta, maxVertDelta);
    this.verticalRate = clamp(
      this.verticalRate,
      -p.maxDescentRate,
      p.maxClimbRate * this.liftScale,
    );

    // 4. World velocity from heading + forward speed + vertical rate.
    _fwd.set(-Math.sin(this.heading), 0, -Math.cos(this.heading));
    this.velocity.copy(_fwd).multiplyScalar(this.forwardSpeed);
    this.velocity.y = this.verticalRate;

    // 5. Integrate position
    this.position.addScaledVector(this.velocity, dt);

    // 6. Visual pitch — flight-path angle plus a small body AoA. Fighters
    //    nose up to climb and nose down to descend, with a few degrees of
    //    AoA on top so the nose sits a bit above the velocity vector even
    //    in level flight. Spring-damped so attitude lags inputs naturally.
    const climbAngle = Math.atan2(this.verticalRate, Math.max(this.forwardSpeed, 1));
    const aoa = p.pitchAoADeg * DEG2RAD;
    const maxPitch = p.maxPitchDeg * DEG2RAD;
    const pitchTarget = clamp(climbAngle + aoa, -maxPitch, maxPitch);
    const pitchAccel = -p.pitchStiffness * (this.pitch - pitchTarget) - p.pitchDamping * this.pitchVel;
    this.pitchVel += pitchAccel * dt;
    this.pitch    += this.pitchVel * dt;

    // 7. Visual roll — coordinated bank. tan(bank) = V·ω/g for a level,
    //    coordinated turn. We approximate with bankPerYawRate then clamp;
    //    spring-damped so banks roll in smoothly.
    const maxBank = p.maxBankDeg * DEG2RAD;
    const rollTarget = clamp(this.yawRate * p.bankPerYawRate, -maxBank, maxBank);
    const rollAccel = -p.rollStiffness * (this.roll - rollTarget) - p.rollDamping * this.rollVel;
    this.rollVel += rollAccel * dt;
    this.roll    += this.rollVel * dt;

    // 8. Random sway — sum-of-sines pseudo-noise for visual life.
    this._swayTime += dt;
    const t = this._swayTime;
    const f = p.swayFrequency * Math.PI * 2;
    const ph = this._swayPhase;
    const swayPitch = p.swayPitchAmp * (Math.sin(t * f       + ph[0]) + 0.5 * Math.sin(t * f * 1.7 + ph[1]));
    const swayRoll  = p.swayRollAmp  * (Math.sin(t * f * 1.1 + ph[2]) + 0.5 * Math.sin(t * f * 1.9 + ph[3]));
    const swayYaw   = p.swayYawAmp   * (Math.sin(t * f * 0.9 + ph[4]) + 0.5 * Math.sin(t * f * 1.5 + ph[5]));

    // 9. Compose quaternion (YXZ: yaw, then pitch, then roll). Sway is
    //    visual-only — flight bookkeeping (heading/pitch/roll) is untouched.
    _euler.set(this.pitch + swayPitch, this.heading + swayYaw, this.roll + swayRoll);
    this.quaternion.setFromEuler(_euler);
  }

  /**
   * Fixed-wing failure mode: ballistic flight along the current velocity
   * vector with a roll spin (and optional flat-spin yaw) layered on top.
   * The nose stays pointed where the plane is going — no random tumble —
   * which reads as "engine/control surfaces dead but airframe intact".
   *
   * Builds the quaternion each frame from heading/pitch derived from
   * velocity, then composes accumulated body-roll and (for flat spin)
   * extra-yaw + nose-up offset.
   */
  _updateFailure(dt) {
    // Ramp the failure forces in. While k < 1 the airframe coasts on its
    // last-controlled velocity and the spin/gravity blend up gradually.
    this._failTransition = Math.min(1, this._failTransition + this._failTransitionRate * dt);
    const k = this._failTransition;

    // Ballistic motion under gravity.
    this.velocity.y -= this.failureGravity * k * dt;
    this.position.addScaledVector(this.velocity, dt);

    // Integrate roll/yaw rates. EnemyPlane jitters the rates each frame
    // to keep the spin from looking metronomic.
    this._failRoll += this._failRollRate * k * dt;
    this._failYaw  += this._failYawRate  * k * dt;

    // Heading + pitch from current velocity vector. atan2(-vx, -vz) maps
    // the world velocity into the same heading convention as level flight
    // (forward = -Z at heading 0). Pitch is the climb angle of the velocity
    // vector — negative for a descending wreck.
    _failVel.copy(this.velocity);
    const horizSpeed = Math.hypot(_failVel.x, _failVel.z);
    const heading = horizSpeed > 0.01
      ? Math.atan2(-_failVel.x, -_failVel.z)
      : this.heading;
    const pitch = horizSpeed > 0.01
      ? Math.atan2(_failVel.y, horizSpeed)
      : 0;

    // Compose: world yaw (heading + extra spin) → body pitch (climb angle
    // + flat-spin nose-up) → body roll. YXZ Euler order matches level flight.
    _euler.set(
      pitch + this._failPitchOffset * k,
      heading + this._failYaw,
      this._failRoll,
    );
    this.quaternion.setFromEuler(_euler);
  }
}
